import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { TouchEvent, useRef } from 'react'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import { DriveHeroTrackEventProperties, UseDriveHeroNewHandlersProps } from '@interfaces/Drivetypes'
import { DRIVE_HERO_ANIMATION_TIMEOUT, MOBILE_SWIPE_THRESHOLD } from './utils'

export const useDriveHeroNewHandlers = ({
  slidesLength,
  activeCarName,
  isAnimating,
  setIsAnimating,
  setCurrentIndex,
}: UseDriveHeroNewHandlersProps) => {
  const touchStartX = useRef<number | null>(null)
  const touchCurrentX = useRef<number | null>(null)

  const trackSwitchEvent = () => {
    void trackEvent('track_event_complete', {
      experiment_name: 'wrapper_v3',
      page_type: 'acko_home_page',
      journey: 'acko_drive_car_switch',
      vehicle_make_model: activeCarName,
    })
  }

  const handleCardClick = async (eventProperties: DriveHeroTrackEventProperties, url: string, urlInNewTab: boolean) => {
    await trackEvent(eventProperties?.eventName ?? 'track_event_complete', {
      experiment_name: 'wrapper_v3',
      page_type: 'acko_home_page',
      redirection_url: url,
      ...eventProperties?.customProperties,
    })

    if (typeof window !== 'undefined' && url) {
      window.open(getUrlWithQueryParams(url), urlInNewTab ? '_blank' : '_self')
    }
  }

  const handlePrevious = () => {
    if (isAnimating) return
    trackSwitchEvent()
    setIsAnimating(true)
    setCurrentIndex(prev => (prev - 1 + slidesLength) % slidesLength)
    setTimeout(() => setIsAnimating(false), DRIVE_HERO_ANIMATION_TIMEOUT)
  }

  const handleNext = () => {
    if (isAnimating) return
    trackSwitchEvent()
    setIsAnimating(true)
    setCurrentIndex(prev => (prev + 1) % slidesLength)
    setTimeout(() => setIsAnimating(false), DRIVE_HERO_ANIMATION_TIMEOUT)
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const startX = event.touches?.[0]?.clientX ?? event.changedTouches?.[0]?.clientX ?? null
    touchStartX.current = startX
    touchCurrentX.current = startX
  }

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    touchCurrentX.current = event.touches?.[0]?.clientX ?? touchCurrentX.current
  }

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return
    const endX = touchCurrentX.current ?? event.changedTouches?.[0]?.clientX ?? touchStartX.current
    const deltaX = endX - touchStartX.current
    touchStartX.current = null
    touchCurrentX.current = null

    if (Math.abs(deltaX) < MOBILE_SWIPE_THRESHOLD) return
    if (deltaX < 0) {
      handleNext()
      return
    }
    handlePrevious()
  }

  return { handleCardClick, handlePrevious, handleNext, handleTouchStart, handleTouchMove, handleTouchEnd }
}
